import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { LoadingSpinner } from '../components/LoadingSpinner';
import toast from 'react-hot-toast';

interface ThumbnailMatch {
    id: number;
    recap_id_1: number;
    recap_id_2: number;
    similarity_score: number;
    match_type: 'EXACT_DUPLICATE' | 'SAME_SERIES' | 'POSSIBLY_RELATED' | 'DIFFERENT';
    confidence: 'HIGH' | 'MEDIUM' | 'LOW';
    created_at: string;
    updated_at: string;
    // Additional data from recaps
    video_id_1: string;
    video_id_2: string;
    title_1: string;
    title_2: string;
    series_name_1: string;
    series_name_2: string;
    channel_name_1: string;
    channel_name_2: string;
    thumbnail_url_1: string;
    thumbnail_url_2: string;
    published_at_1: string;
    published_at_2: string;
}

export const AdminThumbnailMatchesPage: React.FC = () => {
    const { user, isAuthenticated } = useAuth();
    const [matches, setMatches] = useState<ThumbnailMatch[]>([]);
    const [loading, setLoading] = useState(true);
    const [filterType, setFilterType] = useState<string>('all');
    const [sortBy, setSortBy] = useState<string>('similarity');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

    // Check if user is admin
    const isAdmin = Boolean(user?.is_admin);
    if (!isAuthenticated || !user || !isAdmin) {
        return (
            <div className="min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Access Denied</h1>
                    <p className="text-gray-600 dark:text-gray-400">You need admin privileges to access this page.</p>
                </div>
            </div>
        );
    }

    useEffect(() => {
        fetchMatches();
    }, []);

    const fetchMatches = async () => {
        try {
            const response = await fetch('/api/admin/thumbnail-matches', {
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();
                setMatches(data.matches || []);
            } else {
                toast.error('Failed to fetch thumbnail matches');
            }
        } catch (error) {
            console.error('Error fetching matches:', error);
            toast.error('Network error - please check your connection');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteMatch = async (matchId: number) => {
        if (!confirm('Are you sure you want to delete this match?')) return;

        try {
            const response = await fetch(`/api/admin/thumbnail-matches/${matchId}`, {
                method: 'DELETE',
                credentials: 'include'
            });

            if (response.ok) {
                toast.success('Match deleted successfully');
                setMatches(matches.filter(match => match.id !== matchId));
            } else {
                toast.error('Failed to delete match');
            }
        } catch (error) {
            console.error('Error deleting match:', error);
            toast.error('Network error');
        }
    };

    const getConfidenceColor = (confidence: string) => {
        switch (confidence) {
            case 'HIGH': return 'text-green-600 dark:text-green-400';
            case 'MEDIUM': return 'text-yellow-600 dark:text-yellow-400';
            case 'LOW': return 'text-red-600 dark:text-red-400';
            default: return 'text-gray-600 dark:text-gray-400';
        }
    };

    const getMatchTypeColor = (type: string) => {
        switch (type) {
            case 'EXACT_DUPLICATE': return 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-200';
            case 'SAME_SERIES': return 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200';
            case 'POSSIBLY_RELATED': return 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200';
            case 'DIFFERENT': return 'bg-gray-100 dark:bg-gray-900/20 text-gray-800 dark:text-gray-200';
            default: return 'bg-gray-100 dark:bg-gray-900/20 text-gray-800 dark:text-gray-200';
        }
    };

    const filteredMatches = matches.filter(match => {
        if (filterType === 'all') return true;
        return match.match_type === filterType;
    });

    const sortedMatches = [...filteredMatches].sort((a, b) => {
        let comparison = 0;
        switch (sortBy) {
            case 'similarity':
                comparison = a.similarity_score - b.similarity_score;
                break;
            case 'date':
                comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
                break;
            case 'type':
                comparison = a.match_type.localeCompare(b.match_type);
                break;
            default:
                comparison = 0;
        }
        return sortOrder === 'asc' ? comparison : -comparison;
    });

    if (loading) {
        return <LoadingSpinner />;
    }

    return (
        <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-white">
            <div className="max-w-7xl mx-auto px-4 py-8">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                        Thumbnail Duplicate Matches
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400">
                        Visual inspection of thumbnail similarity matches
                    </p>
                </div>

                {/* Filters and Stats */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Filter by type:
                            </label>
                            <select
                                value={filterType}
                                onChange={(e) => setFilterType(e.target.value)}
                                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            >
                                <option value="all">All Types</option>
                                <option value="EXACT_DUPLICATE">Exact Duplicates</option>
                                <option value="SAME_SERIES">Same Series</option>
                                <option value="POSSIBLY_RELATED">Possibly Related</option>
                                <option value="DIFFERENT">Different</option>
                            </select>
                        </div>

                        <div className="flex items-center gap-4">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Sort by:
                            </label>
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            >
                                <option value="similarity">Similarity Score</option>
                                <option value="date">Date Created</option>
                                <option value="type">Match Type</option>
                            </select>
                            <button
                                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            >
                                {sortOrder === 'asc' ? '↑' : '↓'}
                            </button>
                        </div>
                    </div>

                    <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-gray-900 dark:text-white">{matches.length}</div>
                            <div className="text-gray-600 dark:text-gray-400">Total Matches</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                                {matches.filter(m => m.match_type === 'EXACT_DUPLICATE').length}
                            </div>
                            <div className="text-gray-600 dark:text-gray-400">Exact Duplicates</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                                {matches.filter(m => m.match_type === 'SAME_SERIES').length}
                            </div>
                            <div className="text-gray-600 dark:text-gray-400">Same Series</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                                {matches.filter(m => m.match_type === 'POSSIBLY_RELATED').length}
                            </div>
                            <div className="text-gray-600 dark:text-gray-400">Possibly Related</div>
                        </div>
                    </div>
                </div>

                {/* Matches Grid */}
                <div className="space-y-6">
                    {sortedMatches.length === 0 ? (
                        <div className="text-center py-12">
                            <p className="text-gray-600 dark:text-gray-400">No matches found</p>
                        </div>
                    ) : (
                        sortedMatches.map((match) => (
                            <div key={match.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-4">
                                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getMatchTypeColor(match.match_type)}`}>
                                            {match.match_type.replace('_', ' ')}
                                        </span>
                                        <span className={`text-sm font-medium ${getConfidenceColor(match.confidence)}`}>
                                            {match.confidence} Confidence
                                        </span>
                                        <span className="text-sm text-gray-600 dark:text-gray-400">
                                            {(match.similarity_score * 100).toFixed(1)}% Similar
                                        </span>
                                    </div>
                                    <button
                                        onClick={() => handleDeleteMatch(match.id)}
                                        className="px-3 py-1 text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
                                    >
                                        Delete
                                    </button>
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {/* First Video */}
                                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                                        <div className="flex items-start gap-4">
                                            <img
                                                src={match.thumbnail_url_1}
                                                alt={`Thumbnail for ${match.video_id_1}`}
                                                className="w-32 h-20 object-cover rounded-md"
                                                onError={(e) => {
                                                    (e.target as HTMLImageElement).src = '/placeholder-thumbnail.jpg';
                                                }}
                                            />
                                            <div className="flex-1 min-w-0">
                                                <h3 className="font-medium text-gray-900 dark:text-white truncate">
                                                    {match.title_1}
                                                </h3>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                                    Series: {match.series_name_1 || 'Unknown'}
                                                </p>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                                    Channel: {match.channel_name_1}
                                                </p>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                                    Video ID: {match.video_id_1}
                                                </p>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                                    Published: {new Date(match.published_at_1).toLocaleDateString()}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Second Video */}
                                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                                        <div className="flex items-start gap-4">
                                            <img
                                                src={match.thumbnail_url_2}
                                                alt={`Thumbnail for ${match.video_id_2}`}
                                                className="w-32 h-20 object-cover rounded-md"
                                                onError={(e) => {
                                                    (e.target as HTMLImageElement).src = '/placeholder-thumbnail.jpg';
                                                }}
                                            />
                                            <div className="flex-1 min-w-0">
                                                <h3 className="font-medium text-gray-900 dark:text-white truncate">
                                                    {match.title_2}
                                                </h3>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                                    Series: {match.series_name_2 || 'Unknown'}
                                                </p>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                                    Channel: {match.channel_name_2}
                                                </p>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                                    Video ID: {match.video_id_2}
                                                </p>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                                    Published: {new Date(match.published_at_2).toLocaleDateString()}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
                                    Match created: {new Date(match.created_at).toLocaleString()}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}; 